import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Api } from '../../../smoothr-web-app-core/api/api';
import Order from '../../../smoothr-web-app-core/models/Order';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { OrderUtils } from '../../../smoothr-web-app-core/utils/order-utils';
import { Router } from '@angular/router';
import { axiosErrorToMessage, canSkipItemModal, defaultsToArticleOption, numberToCurrency } from '../../../smoothr-web-app-core/utils/utils';
import { AnalyticsService } from '../../../smoothr-web-app-core/services/analytics/analytics.service';
import { CustomerAuth } from '../../../smoothr-web-app-core/models/CustomerAuth';
import Customer from '../../../smoothr-web-app-core/models/Customer';
import { NavigationService, NAVIGATIONURLS } from 'src/app/services/navigation.service';
import { PromoCodeType } from 'src/smoothr-web-app-core/models/PromoCodeType';
import ArticleGroup from 'src/smoothr-web-app-core/models/ArticleGroup';
import Venue from 'src/smoothr-web-app-core/models/Venue';
import Article from 'src/smoothr-web-app-core/models/Article';
import { SelectionProductFromFreeArticleComponent } from '../selection-product-from-free-article/selection-product-from-free-article.component';
import { ModalController } from '@ionic/angular';
import { SelectionMultipleFreeArticlesComponent, SelectedArticleWithQuantity } from '../selection-multiple-free-articles/selection-multiple-free-articles.component';
import ArticleCategory from 'src/smoothr-web-app-core/models/ArticleCategory';
import { AppComponent } from 'src/app/app.component';
import { ModalInfoComponent } from '../modal-info/modal-info.component';

@Component({
	selector: 'app-promo-code',
	templateUrl: './promo-code.component.html',
	styleUrls: ['promo-code.component.scss'],
})
export class PromoCodeComponent implements OnInit, OnDestroy {
	@Input() venue: Venue;
	@Output()
	error = new EventEmitter<string>();
	@Output()
	success = new EventEmitter<Order>();
	@Output()
	deletePromo = new EventEmitter<Order>();
	@Output()
	deleteDependencyPromocode = new EventEmitter<Order>();
	private _customer: Customer;
	numberToCurrency = numberToCurrency;
	ou = OrderUtils;
	get customer(): Customer {
		return this._customer;
	}

	@Input()
	set customer(value) {
		this._customer = value;
		// this.inactive = this._customer?.isAnonymous || !this._customer?.emailVerifiedAt;
		this.inactive = false;
	}

	input: string;
	showInput = false;
	loading = false;
	inactive = true;
	private _error: string;
	private deletePromoSubscription: Subscription;

	constructor(
		private repository: RepositoryService,
		private translate: TranslateService,
		private analytics: AnalyticsService,
		private cdr: ChangeDetectorRef,
		private router: Router,
		private navigationService: NavigationService,
		private modalCtrl: ModalController
	) {}

	private _order: Order;

	get order(): Order {
		return this._order;
	}

	@Input()
	set order(order: Order) {
		this._order = order;
		if (this._order && this._order.promoCode && this._order.promoCode.code) {
			this.input = this._order.promoCode.code;
		}
	}

	get promoError(): string {
		return this._error;
	}

	set promoError(value: string) {
		this._error = value;
		if (this._error) {
			this.loading = false;
			this.error.emit(value);
		}
	}

	ngOnInit() {
		this.deletePromoSubscription = this.deletePromo.subscribe(() => (this.promoError = null));
	}

	ngOnDestroy() {
		this.deletePromoSubscription.unsubscribe();
		this.deletePromoSubscription = null;
	}

	async checkPromoCode() {
		if (!this.input || this.input.replace(' ', '').length === 0) {
			this.promoError = this.translate.instant('promo_code.not_valid');
			return;
		}
		this.loading = true;
		this.promoError = null;
		try {
			const promoCode = (
				await Api.getPromoCode({
					code: this.input,
					venue: this.order.venue,
					customer: this._customer._id,
				})
			).data;
			promoCode.mov = +promoCode.mov;
			if (OrderUtils.orderTotalPrice(this.order, false, false) < promoCode.mov) {
				this.promoError = this.translate.instant('promo_code.mov_not_reached', {
					mov: numberToCurrency(promoCode.mov, this.order.currency),
				});
				return;
			}
			promoCode.customerRedemptionPromoCode = this.input;
			try {
				try {
					const orderCopy = JSON.parse(JSON.stringify(this.order));
					orderCopy.promoCode = promoCode;
					orderCopy.userUid = this.repository.customer.uid;
					orderCopy.orderAt = null;
					orderCopy.customerRedemptionPromoCode = this.input;
					const result = await Api.applyPromoCode(orderCopy);
					if (result.data && promoCode.type === PromoCodeType.CHEAPEST_FREE_ARTICLE) {
						const foundItem = result.data.orderedArticles.find(it => it.freeArticle && it.isPromo);
						if (foundItem) {
							let arrayOfGroupsToAdd: ArticleGroup[] = [];
							this.order.orderedArticles = this.order.orderedArticles.map(it => {
								if (it.article._id === foundItem.article._id) {
									if (it.quantity === 1) {
										it.freeArticle = true;
										it.isPromo = true;
									}
									if (it.quantity > 1) {
										it.quantity = it.quantity - 1;
										const copyOfGroup = JSON.parse(JSON.stringify(it)) as ArticleGroup;
										copyOfGroup.quantity = 1;
										copyOfGroup.freeArticle = true;
										copyOfGroup.isPromo = true;
										arrayOfGroupsToAdd.push(copyOfGroup);
									}
								}
								return it;
							});
							arrayOfGroupsToAdd.forEach(it => {
								this.order.orderedArticles.push(it);
							});
						}
					}
					if (result.data && promoCode.type === PromoCodeType.DEPENDENCY_FREEE_ARTICLE) {
						const foundFree = result.data.orderedArticles.find(it => it.isPromo && it.freeArticle);
						const foundInList =
							promoCode.value &&
							(promoCode.value?.freeArticle === foundFree?.article?._id ||
								promoCode.value?.freeArticle === foundFree?.article?.masterId);
						if (foundInList) {
							let foundArticleInVenue: Article = null;
							this.venue.articleCategories.forEach(cat => {
								let foundProduct = cat.articles.find(
									art => art._id === foundFree?.article?._id || art?.masterId === foundFree?.article?._id
								);
								if (foundProduct) {
									foundArticleInVenue = foundProduct;
								}
							});
							if (foundArticleInVenue) {
								foundFree.article = foundArticleInVenue;
								this.order.orderedArticles.push(foundFree);
							}
						}
					}
					if (result.data && promoCode.type === PromoCodeType.MULTIPLE_FREE_ARTICLES) {
						try {
							let productsRelatedToVenue: Article[] = [];

							this.venue.articleCategories.forEach(cat => {
								cat.articles.forEach(art => {
									if (
										promoCode.value.articles.includes(art._id) ||
										promoCode.value.articles.includes(art.masterId)
									) {
										productsRelatedToVenue.push(art);
									}
								});
							});

							if (productsRelatedToVenue.length > 0) {
								// If there's only 1 unique article, automatically add max quantity
								if (productsRelatedToVenue.length === 1) {
									const singleArticle = productsRelatedToVenue[0];
									const maxQty = promoCode.value.maxQuantity || 1;
									const autoSelected: SelectedArticleWithQuantity[] = [{
										article: singleArticle,
										quantity: maxQty
									}];
									await this.addMultipleFreeArticlesToOrder(autoSelected, promoCode);
									this.loading = false;
									this.showInput = false;
									return;
								} else {
									// Multiple articles to choose from - show popup
									const selectedWithQuantity = await SelectionMultipleFreeArticlesComponent.show(
										this.modalCtrl,
										productsRelatedToVenue,
										this.order,
										promoCode.value.maxQuantity,
										this.venue
									);

									if (selectedWithQuantity && selectedWithQuantity.length > 0) {
										await this.addMultipleFreeArticlesToOrder(selectedWithQuantity, promoCode);
										this.loading = false;
										this.showInput = false;
										return;
									}
								}
							}
						} catch (e) {
							console.log('Error selecting multiple free articles', e);
							return;
						}
					}
					if (result.data && promoCode.type === PromoCodeType.FREE_ARTICLE && promoCode?.value?.length > 1) {
						try {
							let productsRelatedToVenue: Article[] = [];

							this.venue.articleCategories.forEach(cat => {
								cat.articles.forEach(art => {
									if (promoCode.value.includes(art._id) || promoCode.value.includes(art.masterId)) {
										productsRelatedToVenue.push(art);
									}
								});
							});
							const selectedArticle = await SelectionProductFromFreeArticleComponent.show(
								this.modalCtrl,
								productsRelatedToVenue,
								this.order
							);
							try {
								if (selectedArticle) {
									let articleGroup = new ArticleGroup();

									if (selectedArticle.groups.length > 0 && selectedArticle.groups.find(it => it.requiredAmount > 0)) {
										articleGroup = await this.customModalGroup(selectedArticle, null, false, true, true);
									} else {
										articleGroup.article = selectedArticle;
										articleGroup.groups = defaultsToArticleOption(
											selectedArticle,
											[],
											selectedArticle.defaults,
											this.order?.type,
											this.order.preorder.type
										);
										articleGroup.quantity = 1;
										articleGroup.isPromo = true;
										articleGroup.freeArticle = true;
									}
									this.repository.onOrderChange(
										OrderUtils.applyPromo(
											this.translate,
											this.repository._venue,
											this.order,
											promoCode,
											this.analytics,
											articleGroup
										)
									);
									this.loading = false;
									this.showInput = false;
									return;
								}
							} catch (e) {
								console.log('e', e);
							}
						} catch (e) {
							return;
						}
					}
				} catch (e) {
					this.promoError = axiosErrorToMessage(this.translate, e);
					return;
				}
				this.success.emit(OrderUtils.applyPromo(this.translate, this.repository._venue, this.order, promoCode, this.analytics));
				this.loading = false;
				this.showInput = false;
			} catch (e) {
				console.log(e);
				this.promoError = e;
				this.loading = false;
			}
		} catch (e) {
			this.loading = false;
			if (e.response.status === 404) {
				this.promoError = this.translate.instant('promo_code.not_valid');
				return;
			}
			this.promoError = axiosErrorToMessage(this.translate, e);
			return;
		}
	}

	resetError() {
		if (this._error) {
			this.promoError = null;
		}
	}

	editPromo() {
		const promoCode = this.order.promoCode;
		this.order = OrderUtils.removePromo(this.order);
		if (promoCode && promoCode.code) {
			this.order.promoCode = promoCode.code;
		}
		this.showInput = true;
	}

	removePromoAndEmit() {
		if (this.order) {
			try {
				if (
					this.order?.promoCode &&
					[
						PromoCodeType.DEPENDENCY_FREEE_ARTICLE,
						PromoCodeType.FREE_ARTICLE,
						PromoCodeType.MULTIPLE_FREE_ARTICLES
					].includes(this.order?.promoCode?.type)
				) {
					this.deleteDependencyPromocode.emit();
				}
			} catch (e) {}

			this.deletePromo.emit(OrderUtils.removePromo(this.order));
		}
	}

	onAddClick() {
		this.showInput = true;
		this.cdr.detectChanges();
	}

	openLogin() {
		this.navigationService.navigateToUrlWithParams(NAVIGATIONURLS.signIn(), {
			promo: true,
		});
	}

	openReg() {
		this.navigationService.signUp();
	}
	async customModalGroup(
		item: Article,
		category: ArticleCategory,
		addRecomendations: boolean = false,
		isPromo: boolean = false,
		isFree: boolean = false
	): Promise<ArticleGroup | null> {
		const articleGroup = new ArticleGroup();
		articleGroup.article = item;
		articleGroup.groups.push(...defaultsToArticleOption(item, [], item.defaults, this.order?.type, this.order.preorder.type));
		articleGroup.quantity = 1;
		articleGroup.isPromo = true;
		articleGroup.freeArticle = true;
		const modal = await this.modalCtrl.create({
			cssClass: AppComponent.largeScreen ? 'item-modal large-modal' : 'item-modal',
			component: ModalInfoComponent,
			componentProps: {
				articleGroup,
				noQuantity: true,
			},
			mode: 'ios',
			backdropDismiss: true,
		});
		await modal.present();
		const response = await modal.onDidDismiss();

		if (response.data && response.data.articleGroup) {
			return response.data.articleGroup;
		}
		return null;
	}

	private async addMultipleFreeArticlesToOrder(
		selectedArticles: SelectedArticleWithQuantity[],
		promoCode: any
	): Promise<void> {
		for (const selected of selectedArticles) {
			for (let i = 0; i < selected.quantity; i++) {
				let articleGroup = new ArticleGroup();
				articleGroup.article = selected.article;
				articleGroup.quantity = 1;
				articleGroup.isPromo = true;
				articleGroup.freeArticle = true;

				if (
					selected.article.groups.length > 0 &&
					selected.article.groups.find(it => it.requiredAmount > 0) &&
					!canSkipItemModal(selected.article)
				) {
					const customGroup = await this.customModalGroup(
						selected.article,
						null,
						false,
						true,
						true
					);
					if (customGroup) {
						this._order = OrderUtils.applyPromo(
							this.translate,
							this.repository._venue,
							this.order,
							promoCode,
							this.analytics,
							customGroup
						);
						this.repository.onOrderChange(this._order);
					}
				} else {
					articleGroup.groups = defaultsToArticleOption(
						selected.article,
						[],
						selected.article.defaults,
						this.order?.type,
						this.order.preorder.type
					);
					this._order = OrderUtils.applyPromo(
						this.translate,
						this.repository._venue,
						this.order,
						promoCode,
						this.analytics,
						articleGroup
					);
					this.repository.onOrderChange(this._order);
				}
			}
		}
	}

	protected readonly PromoCodeType = PromoCodeType;
}
